/**
 * Work Graph view — WorkUnits and DependencyLinks, readiness, critical path (Doc 17 §6).
 * Click WorkUnit → detail drawer; filters by Initiative, readiness, critical path.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { GitBranch, X, Filter, RefreshCw, AlertCircle } from 'lucide-react';
import { NoPlanCycleState } from '../../components/planning/NoPlanCycleState';
import { PlanningListSkeleton } from '../../components/planning/PlanningListSkeleton';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { getTenantContext } from '../../lib/tenantContext';
import { getFirstActiveCycleProvisional, createDependencyLink, recordPlanningOverride, syncItemDependenciesToPlan, getItemTitlesByIds } from '../../lib/planningService';
import { usePlanningGovernanceAllowed } from '../../hooks/usePlanningGovernanceAllowed';
import { DEPENDENCY_TIMING_LABELS } from '../../lib/sequenceRelationships';
import type { DependencyTypeEnum } from '../../types/planning';
import { getWorkUnitReadiness, getCriticalPathWorkUnitIds, getBlockerCta } from '../../lib/planningQueries';
import {
  listCapabilities,
  listWorkUnitCapabilityRequirements,
  addWorkUnitCapabilityRequirement,
  removeWorkUnitCapabilityRequirement,
  getEligibleUserIdsForWorkUnit,
} from '../../lib/capabilityService';
import type { WorkUnit, Initiative } from '../../types/planning';
import type { WorkUnitReadinessResult } from '../../lib/planningQueries';
import type { WorkUnitCapabilityRequirement } from '../../types/capabilities';
import type { Capability } from '../../types/capabilities';

type ReadinessFilter = 'all' | 'ready' | 'not_ready';

export default function PlanningWorkGraph() {
  const { cycleId: cycleIdFromParams } = useParams<{ cycleId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const cycleIdParam = cycleIdFromParams ?? searchParams.get('cycleId');
  const workUnitIdParam = searchParams.get('workUnitId');
  const canGovern = usePlanningGovernanceAllowed();
  const [cycleId, setCycleId] = useState<string | null>(null);
  const [workUnits, setWorkUnits] = useState<WorkUnit[]>([]);
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [readiness, setReadiness] = useState<WorkUnitReadinessResult[]>([]);
  const [criticalPathIds, setCriticalPathIds] = useState<Set<string>>(new Set());
  const [links, setLinks] = useState<{ from_work_unit_id: string; to_work_unit_id: string; dependency_type: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkUnitId, setSelectedWorkUnitId] = useState<string | null>(workUnitIdParam);
  const [filterInitiativeId, setFilterInitiativeId] = useState<string>('');
  const [filterReadiness, setFilterReadiness] = useState<ReadinessFilter>('all');
  const [filterCriticalPathOnly, setFilterCriticalPathOnly] = useState(false);
  const [planVersionId, setPlanVersionId] = useState<string | null>(null);
  const [editingPlanVersionId, setEditingPlanVersionId] = useState<string | null>(null);
  const [isNonActiveCycle, setIsNonActiveCycle] = useState(false);
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
  const [addDepFromWuId, setAddDepFromWuId] = useState<string>('');
  const [addDepType, setAddDepType] = useState<'FS' | 'SS' | 'FF' | 'SF'>('FS');
  // Finish → Start covers almost every link; the other three wait behind a link.
  const [showTimingOptions, setShowTimingOptions] = useState(false);
  const [addingDep, setAddingDep] = useState(false);
  const [depError, setDepError] = useState<string | null>(null);
  const [showDepOverrideModal, setShowDepOverrideModal] = useState(false);
  const [depOverrideJustification, setDepOverrideJustification] = useState('');
  const [submittingDepOverride, setSubmittingDepOverride] = useState(false);
  const [showReadinessOverrideModal, setShowReadinessOverrideModal] = useState(false);
  const [readinessOverrideJustification, setReadinessOverrideJustification] = useState('');
  const [submittingReadinessOverride, setSubmittingReadinessOverride] = useState(false);
  const [wuRequirements, setWuRequirements] = useState<WorkUnitCapabilityRequirement[]>([]);
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [eligibleUserIds, setEligibleUserIds] = useState<string[]>([]);
  const [addReqCapabilityId, setAddReqCapabilityId] = useState('');
  const [addReqMinLevel, setAddReqMinLevel] = useState(5);
  const [addingReq, setAddingReq] = useState(false);
  const [reqError, setReqError] = useState<string | null>(null);
  const [importingItemDeps, setImportingItemDeps] = useState(false);
  const [importDepsMessage, setImportDepsMessage] = useState<string | null>(null);
  // Map of item_id → display title fetched live from the items table.
  // Keyed by item_id (on Initiative), used to resolve real names for both
  // initiative rows and their child work units.
  const [itemTitles, setItemTitles] = useState<Record<string, string>>({});

  useEffect(() => {
    setSelectedWorkUnitId((prev) => workUnitIdParam || prev);
  }, [workUnitIdParam]);

  const loadWorkGraphData = useCallback((cid: string, pvId: string) => {
    const supabase = getSupabaseClient();
    supabase.from('initiatives').select('*').eq('plan_version_id', pvId).then(({ data: inits }) => {
      const initList = (inits ?? []) as Initiative[];
      setInitiatives(initList);
      // Look up item titles so we show real names instead of ID-based fallbacks; an item
      // the plan holds reads the title it authorized (1060).
      const itemIds = [...new Set(initList.map((i) => i.item_id).filter(Boolean))];
      if (itemIds.length > 0) {
        void getItemTitlesByIds(itemIds, cid).then(({ data: titleMap }) => setItemTitles(titleMap));
      }
      if (initList.length === 0) {
        setWorkUnits([]);
        setLinks([]);
        setReadiness([]);
        setCriticalPathIds(new Set());
        setLoading(false);
        return;
      }
      const initIds = initList.map((i) => i.id);
      supabase.from('work_units').select('*').in('initiative_id', initIds).then((wuRes) => {
        const wuList = (wuRes.data ?? []) as WorkUnit[];
        setWorkUnits(wuList);
        const wuIds = wuList.map((w) => w.id);
        if (wuIds.length === 0) {
          setLinks([]);
          setReadiness([]);
          setCriticalPathIds(new Set());
          setLoading(false);
          return;
        }
        Promise.all([
          supabase.from('dependency_links').select('*').in('from_work_unit_id', wuIds),
          getWorkUnitReadiness(pvId),
          getCriticalPathWorkUnitIds(pvId),
        ]).then(([linkRes, rRes, cpRes]) => {
          const linkRows = (linkRes.data ?? []) as { from_work_unit_id: string; to_work_unit_id: string; dependency_type: string }[];
          const linkData = linkRows.filter((l) => wuIds.includes(l.to_work_unit_id));
          setLinks(linkData);
          setReadiness(rRes.data ?? []);
          setCriticalPathIds(cpRes.data ?? new Set());
          setLoading(false);
        });
      });
    });
  }, []);

  useEffect(() => {
    const overrideCycleId = cycleIdParam ?? selectedCycleId;
    getFirstActiveCycleProvisional(overrideCycleId ? { cycleId: overrideCycleId } : undefined).then(({ data }) => {
      if (!data) {
        setLoading(false);
        return;
      }
      const cid = data.cycle.id;
      setCycleId(cid);
      setPlanVersionId(data.planVersionId);
      setEditingPlanVersionId(data.editingPlanVersionId);
      setIsNonActiveCycle(data.isNonActiveCycle ?? false);
      loadWorkGraphData(cid, data.editingPlanVersionId ?? data.planVersionId ?? '');
    });
  }, [cycleIdParam, selectedCycleId, loadWorkGraphData]);

  const readinessByWu = useMemo(() => new Map(readiness.map((r) => [r.work_unit_id, r])), [readiness]);
  const initById = useMemo(() => new Map(initiatives.map((i) => [i.id, i])), [initiatives]);
  /**
   * Where an initiative sits in this plan's order, 1-based.
   *
   * This used to render `priority_position + 1`, which showed the plan's twelve
   * commitments as #2–#13 — one higher than the plan itself lists them. The
   * column cannot settle it either way: 272 of 515 rows on production are
   * 0-based and the rest are 1-based, so neither keeping nor dropping the +1 is
   * right for all of them. The position within the plan's own ordering is the
   * thing being named, so derive it and stop reading the ambiguous number.
   */
  const initRank = useMemo(() => {
    const ordered = [...initiatives].sort((a, b) => {
      const pa = a.priority_position ?? Number.MAX_SAFE_INTEGER;
      const pb = b.priority_position ?? Number.MAX_SAFE_INTEGER;
      if (pa !== pb) return pa - pb;
      return String(a.id).localeCompare(String(b.id));
    });
    return new Map(ordered.map((i, idx) => [i.id, idx + 1]));
  }, [initiatives]);
  const wuById = useMemo(() => new Map(workUnits.map((w) => [w.id, w])), [workUnits]);
  // Resolve a real display title for a work unit via its parent initiative's item_id.
  const wuDisplayTitle = useCallback(
    (wu: WorkUnit) => {
      const init = initById.get(wu.initiative_id);
      return (init && itemTitles[init.item_id]) || wu.title;
    },
    [initById, itemTitles],
  );
  const initDisplayTitle = useCallback(
    (init: Initiative) => itemTitles[init.item_id] || init.title || `Item ${init.item_id?.slice(0, 8) ?? init.id.slice(0, 8)}`,
    [itemTitles],
  );

  const filteredWorkUnits = useMemo(() => {
    let list = workUnits;
    if (filterInitiativeId) list = list.filter((wu) => wu.initiative_id === filterInitiativeId);
    if (filterReadiness !== 'all') {
      list = list.filter((wu) => {
        const r = readinessByWu.get(wu.id);
        if (filterReadiness === 'ready') return r?.ready === true;
        return r?.ready !== true;
      });
    }
    if (filterCriticalPathOnly) list = list.filter((wu) => criticalPathIds.has(wu.id));
    return list;
  }, [workUnits, filterInitiativeId, filterReadiness, filterCriticalPathOnly, readinessByWu, criticalPathIds]);

  const selectedWu = selectedWorkUnitId ? wuById.get(selectedWorkUnitId) : null;
  const selectedReadiness = selectedWorkUnitId ? readinessByWu.get(selectedWorkUnitId) : null;
  const selectedInit = selectedWu ? initById.get(selectedWu.initiative_id) : null;
  const predecessors = selectedWu ? links.filter((l) => l.to_work_unit_id === selectedWu.id) : [];
  const successors = selectedWu ? links.filter((l) => l.from_work_unit_id === selectedWu.id) : [];

  const openDrawer = (wuId: string) => {
    setSelectedWorkUnitId(wuId);
    const next = new URLSearchParams(searchParams);
    next.set('workUnitId', wuId);
    setSearchParams(next, { replace: true });
  };
  const closeDrawer = () => {
    setSelectedWorkUnitId(null);
    const next = new URLSearchParams(searchParams);
    next.delete('workUnitId');
    setSearchParams(next, { replace: true });
  };

  const workspaceId = getTenantContext()?.workspaceId ?? null;

  useEffect(() => {
    if (workspaceId) {
      listCapabilities(workspaceId).then(({ data }) => setCapabilities(data));
    } else {
      setCapabilities([]);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (!selectedWorkUnitId) {
      setWuRequirements([]);
      setEligibleUserIds([]);
      return;
    }
    listWorkUnitCapabilityRequirements(selectedWorkUnitId).then(({ data }) => setWuRequirements(data));
    getEligibleUserIdsForWorkUnit(selectedWorkUnitId).then(({ data }) => setEligibleUserIds(data ?? []));
  }, [selectedWorkUnitId]);

  const capById = useMemo(() => new Map(capabilities.map((c) => [c.id, c])), [capabilities]);

  async function handleImportItemDependencies() {
    const versionId = editingPlanVersionId ?? planVersionId;
    if (!versionId || !cycleId) return;
    setImportingItemDeps(true);
    setImportDepsMessage(null);
    const { data, error } = await syncItemDependenciesToPlan(versionId);
    setImportingItemDeps(false);
    if (error) {
      setDepError(error);
      return;
    }
    if (data) {
      setImportDepsMessage(
        `${data.created} link(s) created · ${data.skipped} skipped` +
          (data.mirrorPairs > 0 ? ` · ${data.mirrorPairs} mirror pair(s) — see Relationship Health` : ''),
      );
    }
    loadWorkGraphData(cycleId, versionId);
  }

  return (
    <div className="max-w-5xl">
      <PageHeader
        icon={<GitBranch className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50 dark:bg-[var(--surface)]"
        title="Work graph"
        subtitle="Work units, sequence links (prerequisite → dependent), readiness, and critical path."
      />

      {importDepsMessage && (
        <p className="text-sm text-primary-700 bg-primary-50 border border-primary-100 px-4 py-2 mb-4">
          {importDepsMessage}
        </p>
      )}

      {loading ? (
        <PlanningListSkeleton className="min-h-[320px]" />
      ) : !cycleId ? (
        <NoPlanCycleState onSelectCycle={(id) => { setSelectedCycleId(id); setLoading(true); }} />
      ) : (
        <>
        {isNonActiveCycle && (
          <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-sm text-[var(--mustard)]">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            Viewing a non-active plan cycle. Activate it from{' '}
            <Link to="/planning/plans" className="font-medium underline">Planning Home</Link> when ready.
          </div>
        )}
        <div className="space-y-4">
          <div className="flex flex-col lg:flex-row lg:gap-4">
          <div className="flex-1 min-w-0 bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--rule)] bg-[var(--surface)] flex flex-wrap items-center gap-3">
              <span className="font-semibold text-[var(--text-pri)]">Work units ({filteredWorkUnits.length}{filteredWorkUnits.length !== workUnits.length ? ` / ${workUnits.length}` : ''})</span>
              {editingPlanVersionId && (
                <button
                  type="button"
                  onClick={() => void handleImportItemDependencies()}
                  disabled={importingItemDeps}
                  className="text-xs font-medium text-primary-700 hover:underline disabled:opacity-50 inline-flex items-center gap-1"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${importingItemDeps ? 'animate-spin' : ''}`} />
                  Import hard deps from items
                </button>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <Filter className="h-4 w-4 text-[var(--text-ter)]" />
                <select
                  value={filterInitiativeId}
                  onChange={(e) => setFilterInitiativeId(e.target.value)}
                  className="border border-[var(--rule)] rounded px-2 py-1 text-sm"
                >
                  <option value="">All initiatives</option>
                  {initiatives.map((i) => (
                    <option key={i.id} value={i.id}>{initDisplayTitle(i)}</option>
                  ))}
                </select>
                <select
                  value={filterReadiness}
                  onChange={(e) => setFilterReadiness(e.target.value as ReadinessFilter)}
                  className="border border-[var(--rule)] rounded px-2 py-1 text-sm"
                >
                  <option value="all">All readiness</option>
                  <option value="ready">Ready</option>
                  <option value="not_ready">Not ready</option>
                </select>
                <label className="flex items-center gap-1.5 text-sm text-[var(--text-sec)]">
                  <input
                    type="checkbox"
                    checked={filterCriticalPathOnly}
                    onChange={(e) => setFilterCriticalPathOnly(e.target.checked)}
                    className="rounded"
                  />
                  Critical path only
                </label>
              </div>
            </div>
            {workUnits.length === 0 ? (
              <div className="px-4 py-6 text-[var(--text-sec)] text-sm">No work units. Add initiatives and work units in the plan.</div>
            ) : filteredWorkUnits.length === 0 ? (
              <div className="px-4 py-6 text-[var(--text-ter)] text-sm">No work units match the current filters.</div>
            ) : (
              <ul className="divide-y divide-[var(--rule)]">
                {filteredWorkUnits.map((wu) => {
                  const r = readinessByWu.get(wu.id);
                  const onCriticalPath = criticalPathIds.has(wu.id);
                  const init = initById.get(wu.initiative_id);
                  const selected = wu.id === selectedWorkUnitId;
                  return (
                    <li
                      key={wu.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => openDrawer(wu.id)}
                      onKeyDown={(e) => e.key === 'Enter' && openDrawer(wu.id)}
                      className={`px-4 py-3 flex items-center justify-between gap-4 cursor-pointer hover:bg-[var(--surface)] ${selected ? 'bg-primary-50 ring-inset ring-1 ring-primary-200' : ''}`}
                    >
                      <div>
                        <p className="font-medium text-[var(--text-pri)]">{wuDisplayTitle(wu)}</p>
                        <p className="text-xs text-[var(--text-ter)]">{init ? `Initiative #${initRank.get(init.id) ?? '—'}` : ''}</p>
                        {r && !r.ready && r.blockers.length > 0 && (
                          <p className="text-xs text-[var(--mustard)] mt-1">Blocked: {r.blockers.slice(0, 2).join(' · ')}{r.blockers.length > 2 ? '…' : ''}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {onCriticalPath && <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded">Critical path</span>}
                        <span className={`text-xs px-2 py-0.5 rounded ${r?.ready ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--mustard-dim)] text-[var(--mustard)]'}`}>
                          {r?.ready ? 'Ready' : 'Not ready'}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Work unit detail drawer */}
          {selectedWu && (
            <div className="lg:w-96 flex-shrink-0 bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden shadow-lg">
              <div className="px-4 py-3 border-b border-[var(--rule)] bg-[var(--surface)] flex items-center justify-between">
                <span className="font-semibold text-[var(--text-pri)]">Work unit detail</span>
                <button type="button" onClick={closeDrawer} className="p-1 rounded hover:bg-[var(--hover-tint)]" aria-label="Close">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 space-y-4 overflow-y-auto max-h-[70vh]">
                <div>
                  <h3 className="font-medium text-[var(--text-pri)]">{wuDisplayTitle(selectedWu)}</h3>
                  {selectedInit && (
                    <p className="text-sm text-[var(--text-ter)] mt-0.5">Initiative #{initRank.get(selectedInit.id) ?? '—'}</p>
                  )}
                </div>
                {selectedWu.description && (
                  <p className="text-sm text-[var(--text-sec)]">{selectedWu.description}</p>
                )}
                <div>
                  <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Readiness</p>
                  <p className={`text-sm mt-0.5 ${selectedReadiness?.ready ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]'}`}>
                    {selectedReadiness?.ready ? 'Ready' : 'Not ready'}
                  </p>
                  {selectedReadiness && !selectedReadiness.ready && editingPlanVersionId && cycleId && canGovern && (
                    <button
                      type="button"
                      onClick={() => { setShowReadinessOverrideModal(true); setReadinessOverrideJustification(''); }}
                      className="text-xs font-medium text-[var(--mustard)] hover:underline mt-1"
                    >
                      Mark as ready (override)
                    </button>
                  )}
                  {selectedReadiness && !selectedReadiness.ready && selectedReadiness.structured_blockers && selectedReadiness.structured_blockers.length > 0 && cycleId && (
                    <div className="mt-2 space-y-1.5">
                      {selectedReadiness.structured_blockers.map((sb, i) => {
                        const cta = getBlockerCta(sb.type, { cycleId, blockingWorkUnitId: sb.blocking_work_unit_id });
                        return (
                          <div key={i}>
                            <p className="text-xs text-[var(--mustard)]">{sb.reason}</p>
                            <Link to={cta.route} className="text-xs font-medium text-primary-600 hover:underline">
                              {cta.label} →
                            </Link>
                          </div>
                        );
                      })}
                      {editingPlanVersionId && canGovern && selectedReadiness.structured_blockers.some((sb) => sb.type === 'dependency') && (
                        <button
                          type="button"
                          onClick={() => { setShowDepOverrideModal(true); setDepOverrideJustification(''); }}
                          className="text-xs font-medium text-[var(--mustard)] hover:underline"
                        >
                          Request dependency override
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {criticalPathIds.has(selectedWu.id) && (
                  <p className="text-xs px-2 py-1 bg-primary-100 text-primary-700 rounded w-fit">Critical path</p>
                )}
                {predecessors.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Predecessors</p>
                    <ul className="mt-1 text-sm text-[var(--text-sec)] space-y-0.5">
                      {predecessors.map((l, i) => {
                        const from = wuById.get(l.from_work_unit_id);
                        const effType = l.dependency_type as DependencyTypeEnum;
                        return <li key={i}>{from ? wuDisplayTitle(from) : l.from_work_unit_id.slice(0, 8)}… ({DEPENDENCY_TIMING_LABELS[effType].short})</li>;
                      })}
                    </ul>
                  </div>
                )}
                {successors.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Successors</p>
                    <ul className="mt-1 text-sm text-[var(--text-sec)] space-y-0.5">
                      {successors.map((l, i) => {
                        const to = wuById.get(l.to_work_unit_id);
                        const effType = l.dependency_type as DependencyTypeEnum;
                        return <li key={i}>{to ? wuDisplayTitle(to) : l.to_work_unit_id.slice(0, 8)}… ({DEPENDENCY_TIMING_LABELS[effType].short})</li>;
                      })}
                    </ul>
                  </div>
                )}
                {(selectedWu?.estimated_effort_hours != null || selectedWu?.estimated_duration_days != null) && (
                  <div>
                    <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Estimate</p>
                    <p className="text-sm text-[var(--text-sec)] mt-0.5">
                      {selectedWu.estimated_effort_hours != null && `${selectedWu.estimated_effort_hours} hrs`}
                      {selectedWu.estimated_effort_hours != null && selectedWu.estimated_duration_days != null && ' · '}
                      {selectedWu.estimated_duration_days != null && `${selectedWu.estimated_duration_days} days`}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Required capabilities (gate)</p>
                  {wuRequirements.length === 0 ? (
                    <p className="text-sm text-[var(--text-ter)] mt-0.5">None</p>
                  ) : (
                    <ul className="mt-1 text-sm text-[var(--text-sec)] space-y-0.5">
                      {wuRequirements.map((r) => {
                        const cap = capById.get(r.capability_id);
                        return (
                          <li key={r.id} className="flex items-center justify-between gap-2">
                            <span>{cap?.name ?? r.capability_id.slice(0, 8)}… ≥ level {r.min_level}</span>
                            {editingPlanVersionId && (
                              <button
                                type="button"
                                onClick={async () => {
                                  const { error } = await removeWorkUnitCapabilityRequirement(r.id);
                                  if (!error && selectedWorkUnitId) {
                                    listWorkUnitCapabilityRequirements(selectedWorkUnitId).then(({ data }) => setWuRequirements(data));
                                    getEligibleUserIdsForWorkUnit(selectedWorkUnitId).then(({ data }) => setEligibleUserIds(data ?? []));
                                  }
                                }}
                                className="text-xs text-[var(--coral)] hover:underline"
                              >
                                Remove
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {wuRequirements.length > 0 && (
                    <p className="text-xs text-primary-600 mt-1">Eligible: {eligibleUserIds.length} people</p>
                  )}
                  {editingPlanVersionId && capabilities.length > 0 && (
                    <div className="mt-2 space-y-2 border-t border-[var(--rule)] pt-2">
                      <p className="text-xs font-medium text-[var(--text-sec)]">Add requirement</p>
                      <select
                        value={addReqCapabilityId}
                        onChange={(e) => { setAddReqCapabilityId(e.target.value); setReqError(null); }}
                        className="w-full border border-[var(--rule)] rounded px-2 py-1.5 text-sm"
                      >
                        <option value="">Select capability…</option>
                        {capabilities.filter((c) => !wuRequirements.some((r) => r.capability_id === c.id)).map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-[var(--text-sec)]">Min level</label>
                        <select
                          value={addReqMinLevel}
                          onChange={(e) => setAddReqMinLevel(Number(e.target.value))}
                          className="border border-[var(--rule)] rounded px-2 py-1 text-sm"
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          disabled={addingReq || !addReqCapabilityId}
                          onClick={async () => {
                            if (!addReqCapabilityId || !selectedWu) return;
                            setReqError(null);
                            setAddingReq(true);
                            const { error } = await addWorkUnitCapabilityRequirement({
                              work_unit_id: selectedWu.id,
                              capability_id: addReqCapabilityId,
                              min_level: addReqMinLevel,
                            });
                            setAddingReq(false);
                            if (error) setReqError(error);
                            else {
                              setAddReqCapabilityId('');
                              listWorkUnitCapabilityRequirements(selectedWu.id).then(({ data }) => setWuRequirements(data));
                              getEligibleUserIdsForWorkUnit(selectedWu.id).then(({ data }) => setEligibleUserIds(data ?? []));
                            }
                          }}
                          className="px-2 py-1 bg-primary-600 text-white rounded text-sm hover:bg-primary-700 disabled:opacity-50"
                        >
                          {addingReq ? 'Adding…' : 'Add'}
                        </button>
                      </div>
                      {reqError && <p className="text-xs text-[var(--coral)]">{reqError}</p>}
                    </div>
                  )}
                </div>
                {editingPlanVersionId && cycleId && canGovern && (
                  <div className="border-t border-[var(--rule)] pt-3">
                    <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-2">Add sequence link (prerequisite → this unit)</p>
                    <div className="space-y-2">
                      <select
                        value={addDepFromWuId}
                        onChange={(e) => { setAddDepFromWuId(e.target.value); setDepError(null); }}
                        className="w-full border border-[var(--rule)] rounded px-2 py-1.5 text-sm"
                      >
                        <option value="">Prerequisite work unit…</option>
                        {workUnits.filter((w) => w.id !== selectedWu.id).map((w) => (
                          <option key={w.id} value={w.id}>{wuDisplayTitle(w)}</option>
                        ))}
                      </select>
                      {showTimingOptions ? (
                      <select
                        aria-label="Link timing"
                        value={addDepType}
                        onChange={(e) => setAddDepType(e.target.value as 'FS' | 'SS' | 'FF' | 'SF')}
                        className="w-full border border-[var(--rule)] rounded px-2 py-1.5 text-sm"
                      >
                        <option value="FS">{DEPENDENCY_TIMING_LABELS.FS.short} — {DEPENDENCY_TIMING_LABELS.FS.description}</option>
                        <option value="SS">{DEPENDENCY_TIMING_LABELS.SS.short} — {DEPENDENCY_TIMING_LABELS.SS.description}</option>
                        <option value="FF">{DEPENDENCY_TIMING_LABELS.FF.short} — {DEPENDENCY_TIMING_LABELS.FF.description}</option>
                        <option value="SF">{DEPENDENCY_TIMING_LABELS.SF.short} — {DEPENDENCY_TIMING_LABELS.SF.description}</option>
                      </select>
                      ) : (
                        <p className="text-xs text-[var(--text-ter)]">
                          {DEPENDENCY_TIMING_LABELS.FS.description}.{' '}
                          <button type="button" onClick={() => setShowTimingOptions(true)} className="underline hover:text-[var(--text-sec)]">
                            More timing options
                          </button>
                        </p>
                      )}

                      {depError && <p className="text-xs text-[var(--coral)]">{depError}</p>}
                      <button
                        type="button"
                        disabled={addingDep || !addDepFromWuId}
                        onClick={async () => {
                          if (!addDepFromWuId || !editingPlanVersionId) return;
                          setDepError(null);
                          setAddingDep(true);
                          const { error } = await createDependencyLink(editingPlanVersionId, addDepFromWuId, selectedWu.id, addDepType);
                          setAddingDep(false);
                          if (error) {
                            setDepError(error);
                            return;
                          }
                          setAddDepFromWuId('');
                          loadWorkGraphData(cycleId, editingPlanVersionId);
                        }}
                        className="w-full px-2 py-1.5 bg-primary-600 text-white rounded text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                      >
                        {addingDep ? 'Adding…' : 'Add sequence link'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          </div>

          {links.length > 0 && (
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
              <div className="px-4 py-3 border-b border-[var(--rule)] bg-[var(--surface)] font-semibold text-[var(--text-pri)]">
                Sequence links ({links.length})
              </div>
              <ul className="divide-y divide-[var(--rule)] px-4 py-3">
                {links.map((l, i) => {
                  const effType = l.dependency_type as DependencyTypeEnum;
                  return (
                    <li key={`${l.from_work_unit_id}-${l.to_work_unit_id}-${i}`} className="text-sm text-[var(--text-sec)] py-1">
                      {l.from_work_unit_id.slice(0, 8)}… → {l.to_work_unit_id.slice(0, 8)}… ({DEPENDENCY_TIMING_LABELS[effType].short})
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {cycleId && (
            <Link to={`/planning/plans/${cycleId}`} className="text-sm text-primary-600 hover:underline">
              View full plan →
            </Link>
          )}
        </div>
        </>
      )}

      {/* Dependency override modal */}
      {showDepOverrideModal && selectedWu && cycleId && editingPlanVersionId && selectedReadiness?.structured_blockers && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowDepOverrideModal(false)}>
          <div className="bg-[var(--bg-raised)] shadow-lg p-4 max-w-md w-full mx-2" onClick={(e) => e.stopPropagation()}>
            <p className="font-medium text-[var(--text-pri)] mb-2">Request dependency override</p>
            <p className="text-sm text-[var(--text-sec)] mb-3">Record an override for dependency blockers on this work unit. Justification is required.</p>
            <textarea
              value={depOverrideJustification}
              onChange={(e) => setDepOverrideJustification(e.target.value)}
              placeholder="Why is this override needed?"
              className="w-full border border-[var(--rule)] rounded px-2 py-1.5 text-sm min-h-[80px]"
              rows={3}
            />
            <div className="flex gap-2 mt-3 justify-end">
              <button
                type="button"
                onClick={() => setShowDepOverrideModal(false)}
                className="px-3 py-1.5 text-sm border border-[var(--rule)] rounded hover:bg-[var(--surface)]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!depOverrideJustification.trim() || submittingDepOverride}
                onClick={async () => {
                  const justification = depOverrideJustification.trim();
                  if (!justification || !selectedWu) return;
                  setSubmittingDepOverride(true);
                  const impactedIds = [selectedWu.id, ...(selectedReadiness?.structured_blockers?.filter((sb): sb is typeof sb & { blocking_work_unit_id: string } => !!sb.blocking_work_unit_id).map((sb) => sb.blocking_work_unit_id) ?? [])];
                  const { error } = await recordPlanningOverride({
                    plan_cycle_id: cycleId,
                    plan_version_id: editingPlanVersionId,
                    override_type: 'DependencyOverride',
                    justification,
                    payload: { impacted_object_ids: impactedIds },
                  });
                  setSubmittingDepOverride(false);
                  if (error) return;
                  setShowDepOverrideModal(false);
                  setDepOverrideJustification('');
                }}
                className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {submittingDepOverride ? 'Submitting…' : 'Submit override'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual readiness override modal */}
      {showReadinessOverrideModal && selectedWu && cycleId && editingPlanVersionId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowReadinessOverrideModal(false)}>
          <div className="bg-[var(--bg-raised)] shadow-lg p-4 max-w-md w-full mx-2" onClick={(e) => e.stopPropagation()}>
            <p className="font-medium text-[var(--text-pri)] mb-2">Manual readiness override</p>
            <p className="text-sm text-[var(--text-sec)] mb-3">Record that this work unit is marked ready despite blockers. Justification is required.</p>
            <textarea
              value={readinessOverrideJustification}
              onChange={(e) => setReadinessOverrideJustification(e.target.value)}
              placeholder="Why is this override needed?"
              className="w-full border border-[var(--rule)] rounded px-2 py-1.5 text-sm min-h-[80px]"
              rows={3}
            />
            <div className="flex gap-2 mt-3 justify-end">
              <button
                type="button"
                onClick={() => setShowReadinessOverrideModal(false)}
                className="px-3 py-1.5 text-sm border border-[var(--rule)] rounded hover:bg-[var(--surface)]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!readinessOverrideJustification.trim() || submittingReadinessOverride}
                onClick={async () => {
                  const justification = readinessOverrideJustification.trim();
                  if (!justification || !selectedWu) return;
                  setSubmittingReadinessOverride(true);
                  const { error } = await recordPlanningOverride({
                    plan_cycle_id: cycleId,
                    plan_version_id: editingPlanVersionId,
                    override_type: 'ManualReadinessOverride',
                    justification,
                    payload: { impacted_object_ids: [selectedWu.id] },
                  });
                  setSubmittingReadinessOverride(false);
                  if (error) return;
                  setShowReadinessOverrideModal(false);
                  setReadinessOverrideJustification('');
                }}
                className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {submittingReadinessOverride ? 'Submitting…' : 'Submit override'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
